/**
 * Adaptive Card Detection Utilities
 * 
 * Provides functions to detect and extract Adaptive Card payloads from API responses.
 * Supports multiple response formats:
 * - Direct Adaptive Card JSON with type: "AdaptiveCard"
 * - Bot Framework attachments with contentType: "application/vnd.microsoft.card.adaptive"
 * - Nested card structures in meta.adaptiveCard
 */

import type { Message } from '../interfaces';

/**
 * Check if a message contains an Adaptive Card payload
 * @param message - The message object to check
 * @returns true if message contains an Adaptive Card
 */
export function isAdaptiveCardMessage(message: Message): boolean {
  if (!message) return false;

  // Check meta.adaptiveCard property
  if (message.meta?.adaptiveCard && isValidAdaptiveCardJson(message.meta.adaptiveCard)) {
    return true;
  }

  // Check attachments array (Bot Framework format)
  if (Array.isArray(message.attachments) && message.attachments.length > 0) {
    const hasAdaptiveCard = message.attachments.some(
      att => att.contentType === 'application/vnd.microsoft.card.adaptive' && att.content
    );
    if (hasAdaptiveCard) return true;
  }

  // Check if content itself is an Adaptive Card JSON (edge case)
  if (typeof message.content === 'object' && isValidAdaptiveCardJson(message.content)) {
    return true;
  }

  return false;
}

/**
 * Extract the Adaptive Card JSON from a message
 * @param message - The message object
 * @returns The Adaptive Card JSON object or null
 */
export function extractAdaptiveCardJson(message: Message): any | null {
  if (!message) return null;

  // Priority 1: Check meta.adaptiveCard
  if (message.meta?.adaptiveCard && isValidAdaptiveCardJson(message.meta.adaptiveCard)) {
    return message.meta.adaptiveCard;
  }

  // Priority 2: Check attachments array (Bot Framework format)
  if (Array.isArray(message.attachments) && message.attachments.length > 0) {
    const adaptiveCardAttachment = message.attachments.find(
      att => att.contentType === 'application/vnd.microsoft.card.adaptive' && att.content
    );
    if (adaptiveCardAttachment && isValidAdaptiveCardJson(adaptiveCardAttachment.content)) {
      return adaptiveCardAttachment.content;
    }
  }

  // Priority 3: Check if content itself is Adaptive Card JSON
  if (typeof message.content === 'object' && isValidAdaptiveCardJson(message.content)) {
    return message.content;
  }

  return null;
}

/**
 * Validate if an object is a valid Adaptive Card JSON
 * @param obj - Object to validate
 * @returns true if object is a valid Adaptive Card
 */
function isValidAdaptiveCardJson(obj: any): boolean {
  if (!obj || typeof obj !== 'object') return false;

  // Check for required Adaptive Card properties
  if (obj.type === 'AdaptiveCard') {
    // Must have version and body
    return !!(obj.version && (Array.isArray(obj.body) || Array.isArray(obj.actions)));
  }

  return false;
}

/**
 * Get fallback text from an Adaptive Card message
 * Uses the message content as fallback or constructs one from card structure
 * @param message - The message object
 * @returns Fallback text string
 */
export function getAdaptiveCardFallbackText(message: Message): string {
  // If content is a string, use it as fallback
  if (typeof message.content === 'string' && message.content.trim()) {
    return message.content;
  }

  // Try to extract text from card body (first TextBlock or text-like element)
  const cardJson = extractAdaptiveCardJson(message);
  if (cardJson && Array.isArray(cardJson.body)) {
    const firstTextBlock = cardJson.body.find((item: any) => 
      item.type === 'TextBlock' && item.text
    );
    if (firstTextBlock) {
      return firstTextBlock.text;
    }
  }

  // Default fallback
  return 'Interactive card content';
}

// Generated by Copilot
// updated on: 2026-01-01 | 110+ lines added | New Utility | Created Adaptive Card detection and extraction utilities supporting multiple API response formats | Utility Functions
